import { type MenuProps } from 'antd';

import type { SupportedLanguage } from '../../../translate';
import type {
  EpiDocumentType,
  EpiPredefinedEntry,
  EpiSectionNode,
} from '../types/types';
import { CUSTOM_SECTION_TITLE } from '../types/constants';
import { text } from './epiHelpers';
import {
  getAvailableOptionalSections,
  getDefaultTitle,
  getParentSectionsSchema,
  getPredefinedEntries,
  getSchemaSectionAtPath,
  resolveSectionDef,
  takesNatureType,
} from './schemaUtils';

type MenuItem = NonNullable<MenuProps['items']>[number];

/**
 * Configuration for dropdown menu items
 */
interface IDropdownMenuConfig {
  docType: EpiDocumentType;
  /** The language being edited — add-menu labels come from schema defaults. */
  lang: SupportedLanguage;
  onAddOptional: (
    parentPath: string,
    key: string,
    repeatable: boolean,
    title: string
  ) => void;
  onAddCustom: (parentPath: string, title: string) => void;
  onAddPredefined?: (host: EpiSectionNode, entry: EpiPredefinedEntry) => void;
  /** Keeps an imported unmatched heading as a free custom section. */
  keepCustomSection?: (path: string) => void;
  /** Adds a `key` block printing right after the block at `blockPath`. */
  onAddBlockAfter?: (blockPath: string, key: string) => void;
  /** Whether this screen offers free-form subsections. Removal is unaffected. */
  canAddCustomSections?: boolean;
  removeSection: (
    path: string,
    key: string,
    isCustom: boolean,
    customSectionId?: string
  ) => void;
  /**
   * Sibling nodes at this tree level. Used to count instances of a repeatable
   * key, which decides whether a mandatory block may be removed.
   */
  siblings?: EpiSectionNode[];
  /** Opens the nature/type editor for a repeatable block instance. */
  editNatureType?: (node: EpiSectionNode) => void;
  /** Moves a custom section one place up (-1) or down (1) within its parent. */
  moveCustomSection?: (path: string, direction: -1 | 1) => void;
}

/**
 * Creates "Add" menu items for optional sections not yet present in data
 */
const createAddItems = (
  node: EpiSectionNode,
  menuConfig: IDropdownMenuConfig
): MenuProps['items'] => {
  const { docType, onAddOptional } = menuConfig;

  // Get schema for this section's children
  const schemaDef = getSchemaSectionAtPath(docType, node.path);
  if (!schemaDef?.properties?.sections) return [];

  const sectionsSchema = schemaDef.properties.sections;
  const schemaSections = sectionsSchema.properties;

  // Get data sections from children (already present)
  // Presence is all getAvailableOptionalSections looks at.
  const dataSections: Record<string, true> = {};
  for (const child of node.children) {
    if (!child.isCustom) {
      dataSections[child.key] = true;
    }
  }

  const available = getAvailableOptionalSections(
    schemaSections,
    sectionsSchema,
    dataSections,
    menuConfig.lang
  );

  return available.map(({ key, title, repeatable }) => ({
    key: `add-${key}`,
    label: `Add ${title}`,
    onClick: () => onAddOptional(node.path, key, repeatable, title),
  }));
};

/**
 * "Add custom section". Where the host has predefined entries not yet present,
 * it opens onto them, then the free option.
 */
const createCustomItem = (
  node: EpiSectionNode,
  menuConfig: IDropdownMenuConfig
): MenuItem | null => {
  if (!node.allowCustom) return null;
  if (menuConfig.canAddCustomSections === false) return null;

  const { onAddCustom, onAddPredefined, docType, lang } = menuConfig;
  const free = {
    key: `custom-${node.path}`,
    label: 'Add custom section',
    onClick: () => onAddCustom(node.path, CUSTOM_SECTION_TITLE),
  };

  const present = new Set(node.children.map(child => child.predefinedKey));
  const available = getPredefinedEntries(docType, node).filter(
    entry => !present.has(entry.key)
  );
  if (!onAddPredefined || available.length === 0) return free;

  return {
    key: `custom-${node.path}`,
    label: 'Add custom section',
    children: [
      ...available.map(entry => ({
        key: `predefined-${node.path}-${entry.key}`,
        label: text(entry.title, lang),
        onClick: () => onAddPredefined(node, entry),
      })),
      { type: 'divider' as const },
      { ...free, key: `free-${node.path}`, label: 'Free section' },
    ],
  };
};

/** "Keep as custom section" — an imported heading no predefined entry matched. */
const createKeepItem = (
  node: EpiSectionNode,
  menuConfig: IDropdownMenuConfig
): MenuItem | null => {
  const { keepCustomSection } = menuConfig;
  if (!node.unmatched || !keepCustomSection) return null;
  return {
    key: `keep-${node.path}`,
    label: 'Keep as custom section',
    onClick: () => keepCustomSection(node.path),
  };
};

/**
 * "Add … after this block", on a packaging block, for each repeatable key of
 * the level that takes no nature/type — the patient card. Adding one from the
 * parent appends it after every block, which is wrong as soon as a Labelling
 * covers more than one strength.
 */
const createBlockAfterItems = (
  node: EpiSectionNode,
  menuConfig: IDropdownMenuConfig
): MenuItem[] => {
  const { onAddBlockAfter, docType, lang } = menuConfig;
  if (!node.repeatable || !onAddBlockAfter) return [];
  const level = getParentSectionsSchema(docType, node.path)?.properties ?? {};
  return Object.entries(level)
    .filter(([key]) => key !== node.key)
    .map(([key, entry]) => ({ key, ...resolveSectionDef(entry) }))
    .filter(({ def, repeatable }) => repeatable && !takesNatureType(def))
    .map(({ key, def }) => ({
      key: `add-after-${node.path}-${key}`,
      label: `Add ${text(getDefaultTitle(def), lang)} after this block`,
      onClick: () => onAddBlockAfter(node.path, key),
    }));
};

/**
 * "Change nature/type" entry — repeatable block instances only. Lets the user
 * label the block that was seeded automatically at create time, or correct a
 * label afterwards.
 */
const createNatureTypeItem = (
  node: EpiSectionNode,
  menuConfig: IDropdownMenuConfig
): MenuItem | null => {
  if (!node.hasNatureType) return null;
  const { editNatureType } = menuConfig;
  if (!editNatureType) return null;

  return {
    key: `nature-${node.path}`,
    label: 'Change nature/type',
    onClick: () => editNatureType(node),
  };
};

/**
 * Creates remove menu item if section is not mandatory. Repeatable mandatory
 * blocks (Labelling outer/immediate packaging, `1..*`) are the exception: they
 * can be removed while more than one instance exists, since removing the last
 * one would breach the required minimum.
 */
const createRemoveItem = (
  node: EpiSectionNode,
  menuConfig: IDropdownMenuConfig
): MenuItem[] => {
  if (node.isMandatory) {
    const sameKeyCount = (menuConfig.siblings ?? []).filter(
      sibling => !sibling.isCustom && sibling.key === node.key
    ).length;
    if (!node.repeatable || sameKeyCount < 2) return [];
  }

  const { removeSection } = menuConfig;

  return [
    {
      key: `delete-${node.path}`,
      label: 'Delete section',
      onClick: () =>
        removeSection(
          node.path,
          node.key,
          node.isCustom,
          node.isCustom ? node.key : undefined
        ),
    },
  ];
};

/**
 * Move up / Move down — free and unmatched custom sections only. Declared
 * sections keep the QRD's order, and predefined ones the list's until ALIMS
 * says whether they may move. A move never leaves the parent: `siblings` is
 * the parent's children in print order, so the edges are its first and last.
 */
const createMoveItems = (
  node: EpiSectionNode,
  menuConfig: IDropdownMenuConfig
): MenuItem[] => {
  const { moveCustomSection, siblings = [] } = menuConfig;
  if (!node.isCustom || node.predefinedKey || !moveCustomSection) return [];
  const position = siblings.findIndex(sibling => sibling.path === node.path);

  return [
    {
      key: `move-up-${node.path}`,
      label: 'Move up',
      disabled: position <= 0,
      onClick: () => moveCustomSection(node.path, -1),
    },
    {
      key: `move-down-${node.path}`,
      label: 'Move down',
      disabled: position < 0 || position >= siblings.length - 1,
      onClick: () => moveCustomSection(node.path, 1),
    },
  ];
};

/**
 * Builds dropdown menu items for a section node
 */
export const buildDropdownItems = (
  node: EpiSectionNode,
  menuConfig: IDropdownMenuConfig
): MenuProps['items'] => {
  const addItems = createAddItems(node, menuConfig);
  const blockAfterItems = createBlockAfterItems(node, menuConfig);
  const natureTypeItem = createNatureTypeItem(node, menuConfig);
  const customItem = createCustomItem(node, menuConfig);
  const keepItem = createKeepItem(node, menuConfig);
  const removeItems = createRemoveItem(node, menuConfig);
  const moveItems = createMoveItems(node, menuConfig);

  return [
    ...(addItems || []),
    ...blockAfterItems,
    ...(natureTypeItem ? [natureTypeItem] : []),
    ...(keepItem ? [keepItem] : []),
    ...moveItems,
    ...removeItems,
    ...(customItem ? [customItem] : []),
  ];
};
